"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useOnborda } from "onborda";
import { useSession } from "next-auth/react";
import { useParams, usePathname, useRouter } from "next/navigation";

import { isOnboardingV2Incomplete } from "@/data/onboarding-v2";
import { useProductsByOrgId } from "@/hooks/use-products-by-org-id";
import {
  hrefPreservingPinnedChatLayout,
  requestExpandAppSidebar,
} from "@/lib/chat/chat-layout-persistence";
import {
  isProductIntroSuppressed,
  isProductOnboardingRoute,
  PRODUCT_INTRO_REMIND_MS,
  PRODUCTS_TOUR_ID,
  writeProductIntroDismissal,
} from "@/lib/product-onboarding-nudge";
import { isValidOrgId } from "@/lib/utils";

const SPLASH_DELAY_MS = 800;

export interface UseProductOnboardingNudgeResult {
  readonly open: boolean;
  readonly handleCreateProduct: () => void;
  readonly handleShowMeWhere: () => void;
  readonly handleRemindLater: () => void;
  readonly handleOpenChange: (nextOpen: boolean) => void;
}

export function useProductOnboardingNudge(): UseProductOnboardingNudgeResult {
  const router = useRouter();
  const pathname = usePathname();
  const { orgId: routeOrgId } = useParams();
  const orgId = isValidOrgId(routeOrgId as string | undefined)
    ? (routeOrgId as string)
    : undefined;
  const { data: session, status: sessionStatus } = useSession();
  const email = session?.user?.email ?? null;
  const isAuthPending = sessionStatus === "loading";
  const { products, isLoading: isProductsLoading } = useProductsByOrgId(orgId);
  const { startOnborda } = useOnborda();
  const [open, setOpen] = useState(false);
  const isIntentionalDismissRef = useRef(false);

  const { data: onboardingIncomplete, isLoading: isOnboardingLoading } =
    useQuery({
      queryKey: ["onboardingV2Incomplete", email],
      queryFn: () => isOnboardingV2Incomplete(email!),
      enabled: Boolean(email),
      staleTime: 60_000,
    });

  useEffect(() => {
    setOpen(false);
  }, [orgId]);

  const shouldOfferNudge = useMemo(() => {
    if (!orgId || isAuthPending) {
      return false;
    }

    if (sessionStatus === "unauthenticated") {
      return false;
    }

    if (isProductsLoading) {
      return false;
    }

    if (email && isOnboardingLoading) {
      return false;
    }

    if (products.length > 0) {
      return false;
    }

    if (onboardingIncomplete === true) {
      return false;
    }

    if (isProductOnboardingRoute(pathname)) {
      return false;
    }

    if (isProductIntroSuppressed(orgId)) {
      return false;
    }

    return true;
  }, [
    orgId,
    isAuthPending,
    sessionStatus,
    isProductsLoading,
    email,
    isOnboardingLoading,
    products.length,
    onboardingIncomplete,
    pathname,
  ]);

  useEffect(() => {
    if (!shouldOfferNudge) {
      return;
    }

    const timer = globalThis.setTimeout(() => {
      setOpen(true);
    }, SPLASH_DELAY_MS);

    return (): void => {
      globalThis.clearTimeout(timer);
    };
  }, [shouldOfferNudge]);

  useEffect(() => {
    if (products.length > 0) {
      setOpen(false);
    }
  }, [products.length]);

  const dismissPermanently = useCallback((): void => {
    if (!orgId) {
      return;
    }

    isIntentionalDismissRef.current = true;
    writeProductIntroDismissal(orgId, { kind: "permanent" });
    setOpen(false);
  }, [orgId]);

  const handleCreateProduct = useCallback((): void => {
    if (!orgId) {
      return;
    }

    dismissPermanently();
    router.push(
      hrefPreservingPinnedChatLayout(`/${orgId}/products/new`),
    );
  }, [dismissPermanently, orgId, router]);

  const startProductsTour = useCallback((): void => {
    try {
      localStorage.setItem("dashboardSidebarOpen", "true");
    } catch {
      // ignore (e.g. private mode)
    }

    requestExpandAppSidebar();

    globalThis.setTimeout(() => {
      startOnborda(PRODUCTS_TOUR_ID);
    }, 350);
  }, [startOnborda]);

  const handleShowMeWhere = useCallback((): void => {
    dismissPermanently();
    startProductsTour();
  }, [dismissPermanently, startProductsTour]);

  const handleRemindLater = useCallback((): void => {
    if (!orgId) {
      return;
    }

    isIntentionalDismissRef.current = true;
    writeProductIntroDismissal(orgId, {
      kind: "snooze",
      until: Date.now() + PRODUCT_INTRO_REMIND_MS,
    });
    setOpen(false);
  }, [orgId]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (nextOpen) {
        setOpen(true);
        return;
      }

      if (isIntentionalDismissRef.current) {
        isIntentionalDismissRef.current = false;
        setOpen(false);
        return;
      }

      handleRemindLater();
    },
    [handleRemindLater],
  );

  return {
    open,
    handleCreateProduct,
    handleShowMeWhere,
    handleRemindLater,
    handleOpenChange,
  };
}
